import * as Sentry from '@sentry/nextjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { setupFrontend } from '@/api/setup/setup-frontend';
import { fetchUserRolesAndPermissions } from '@/api/user';
import { setAuthToken } from '@/auth';
import { setUniversalCookie } from '@/cookies';
import { plans } from '@/fixtures/plans';
import { log } from '@/lib/utils';
import { setOwnedReferralCodes } from '@/utils/helpers';
import { userKeys } from '@/utils/queryKeys';

import { buildUserDataCookie } from './buildUserDataCookie';
import { useGTag } from '../analytics/useGTag';

import type { Permission, Role } from '@/types/auth';
import type { Settings } from '@/types/settings';

interface UserSessionData {
  permissions: Permission[] | Permission;
  role: Role[] | Role;
}

export interface SettingsQueryResult {
  siteSettings: Settings;
  userSessionData?: UserSessionData | null;
}

export const CACHE_KEY = userKeys.settings();

const getErrorDetails = (
  e: unknown
): {
  errorCode: string | undefined;
  errorMessage: string | undefined;
  errorStatus: number | undefined;
} => {
  // Log critical settings fetch failures
  // If the user hit a network error, log the error code, message, and status code
  let errorCode: string | undefined;
  let errorMessage: string | undefined;
  let errorStatus: number | undefined;

  if (e && typeof e === 'object') {
    // Axios or fetch error
    if ('code' in e && typeof e.code === 'string') {
      errorCode = e.code;
    }
    if ('message' in e && typeof e.message === 'string') {
      errorMessage = e.message;
    }
    if ('status' in e && typeof e.status === 'number') {
      errorStatus = e.status;
    } else if (
      'response' in e &&
      e.response &&
      typeof e.response === 'object' &&
      typeof (e.response as { status: number })?.status === 'number'
    ) {
      errorStatus = (e.response as { status: number })?.status;
    }
  } else {
    errorMessage = e instanceof Error ? e.message : 'Unknown error';
  }

  return {
    errorCode,
    errorMessage,
    errorStatus,
  };
};

const getInitialSettingsFromLocalStorage = (): Settings | undefined => {
  if (typeof localStorage !== 'undefined') {
    const savedSettings = localStorage.getItem('settings');

    if (savedSettings) {
      try {
        return JSON.parse(savedSettings) as Settings;
      } catch (error) {
        console.warn('Failed to parse settings from localStorage', error);
      }
    }
  }

  return;
};

export const useSettings = ({
  initialData: initialSiteSettings,
}: {
  initialData?: Settings;
} = {}) => {
  const queryClient = useQueryClient();
  const isGTagReady = useGTag();

  const handleUserData = (data: Settings | undefined) => {
    if (!data) {
      return;
    }

    if (data.user?.is_authenticated && data.user.auth_token) {
      setAuthToken(data.user.auth_token);
    }

    if (data.user?.is_authenticated) {
      Sentry.setUser({ id: data.user.id });
    }

    // Persist the user's own referral code so validateReferralCode() can
    // detect self-referrals at registration time (same browser).
    if (data.user?.referralCode) {
      setOwnedReferralCodes(data.user.referralCode);
    }
  };

  const handleLocalStorageAndCookies = (data: Settings | undefined) => {
    if (typeof document !== 'undefined' && data) {
      try {
        setUniversalCookie('userData', buildUserDataCookie(data), {
          options: { sameSite: 'strict' },
        });

        setUniversalCookie('currency', data?.currency, {
          options: { sameSite: 'strict' },
        });

        const dataToSave: Settings = {
          ...data,
          user: { ...data.user, auth_token: undefined },
        };

        localStorage.setItem('settings', JSON.stringify(dataToSave));
      } catch (e) {
        console.warn('Something went wrong setting cookies/local storage.', e);
      }
    }
  };

  const {
    data: queryResult,
    isLoading,
    isFetching,
    error,
  } = useQuery<SettingsQueryResult>({
    queryKey: userKeys.settings(),
    queryFn: async (): Promise<SettingsQueryResult> => {
      try {
        log('📡 [useSettings] Starting settings query...');

        const siteSettings = await setupFrontend();
        let userSessionData: UserSessionData | null = null;

        log('📦 [useSettings] Settings fetched:', {
          isAuthenticated: siteSettings?.user?.is_authenticated,
          userId: siteSettings?.user?.id,
          userEmail: siteSettings?.user?.email,
        });

        handleUserData(siteSettings);
        handleLocalStorageAndCookies(siteSettings);

        if (siteSettings?.user?.id) {
          try {
            userSessionData = await fetchUserRolesAndPermissions(
              siteSettings.user.id
            );
            log('✅ [useSettings] User session data fetched successfully:', {
              roles: userSessionData?.role,
              permissions: userSessionData?.permissions,
            });

            // Re-write the userData cookie now that session data has loaded.
            // The first write (handleLocalStorageAndCookies) runs before
            // roles/permissions are fetched, so enrich it here with
            // role/permissions and the derived subscriptionType — SSR
            // GrowthBook targeting reads these back off the cookie.
            if (typeof document !== 'undefined' && userSessionData) {
              const sessionRoles = Array.isArray(userSessionData.role)
                ? userSessionData.role
                : userSessionData.role
                  ? [userSessionData.role]
                  : [];
              const sessionPermissions = Array.isArray(
                userSessionData.permissions
              )
                ? userSessionData.permissions
                : userSessionData.permissions
                  ? [userSessionData.permissions]
                  : [];
              const foundPlan = plans.find((plan) =>
                sessionRoles.includes(plan.name as Role)
              );
              const subscriptionType = foundPlan?.name ?? 'Free';

              setUniversalCookie(
                'userData',
                buildUserDataCookie(siteSettings, {
                  subscriptionType,
                  role: sessionRoles,
                  permissions: sessionPermissions,
                }),
                { options: { sameSite: 'strict' } }
              );
            }
          } catch (e) {
            log('❌ [useSettings] Failed to fetch user session data:', e);

            // Log when authenticated user can't fetch session data
            Sentry.captureException(e, {
              tags: {
                auth_issue: 'session_data_fetch_failed',
                component: 'useSettings',
              },
              extra: {
                userId: siteSettings?.user?.id,
                userEmail: siteSettings?.user?.email,
                isAuthenticated: siteSettings?.user?.is_authenticated,
              },
            });
          }
        } else {
          log('ℹ️ [useSettings] No user ID - skipping session data fetch');
        }

        return {
          siteSettings: siteSettings || undefined,
          userSessionData,
        };
      } catch (e) {
        log(
          '❌ [useSettings] Failed to fetch settings - network or server error:',
          e
        );

        // Only report actionable errors to Sentry (server 4xx/5xx via FetchError).
        // Skip client-side network failures caused by ad blockers, browser
        // extensions, or connectivity issues — these are never actionable and
        // generate significant Sentry noise. The error message varies by browser:
        // - Chromium: "Failed to fetch"
        // - Safari: "Load failed"
        // - Firefox: "NetworkError when attempting to fetch resource."
        const isNetworkError =
          e instanceof TypeError &&
          /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/.test(
            e.message
          );

        if (!isNetworkError) {
          const { errorCode, errorMessage, errorStatus } = getErrorDetails(e);

          Sentry.captureException(e, {
            tags: {
              auth_issue: 'settings_fetch_failed',
              component: 'useSettings',
            },
            extra: {
              errorCode,
              errorMessage,
              errorStatus,
              hasInitialData: !!initialSiteSettings,
              userAgent:
                typeof window === 'undefined'
                  ? 'unknown'
                  : window.navigator.userAgent,
              url:
                typeof window === 'undefined'
                  ? 'unknown'
                  : window.location.href,
            },
          });
        }

        throw e; // Re-throw to let TanStack Query handle retry logic
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - settings don't change often
    initialDataUpdatedAt: 0,
    initialData: initialSiteSettings
      ? {
          siteSettings: initialSiteSettings,
          userSessionData: undefined,
        }
      : undefined,
    placeholderData: (() => {
      const placeholderSiteSettings = getInitialSettingsFromLocalStorage();

      return placeholderSiteSettings
        ? {
            siteSettings: placeholderSiteSettings,
            userSessionData: undefined,
          }
        : undefined;
    })(),
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isGTagReady && queryResult?.siteSettings?.user?.email && window.gtag) {
      window.gtag('set', 'user_data', {
        email: queryResult.siteSettings.user.email,
      });
    }
  }, [isGTagReady, queryResult?.siteSettings?.user?.email]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const email = queryResult?.siteSettings?.user?.email;
    if (email) {
      const _hsq = (window._hsq = window._hsq || []);
      _hsq.push(['identify', { email }]);
      _hsq.push(['trackPageView']);
    }
  }, [queryResult?.siteSettings?.user?.email]);

  const invalidateSettings = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: CACHE_KEY });
  }, [queryClient]);

  // refetchType 'all' so the refetch is awaited even when no component is
  // currently observing settings — callers (signup, login) rely on the
  // resolved value being in the cache before they route onwards (DEV-6741).
  const invalidateSettingsAsync = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: CACHE_KEY,
      refetchType: 'all',
    });

    return;
  }, [queryClient]);

  // The signed-in user's id, refetching settings first when it isn't loaded
  // yet (e.g. straight after sign-up, before settings have refreshed)
  const getUserIdAsync = useCallback(async (): Promise<number | undefined> => {
    const loadedId = queryResult?.siteSettings?.user?.id;

    if (loadedId) {
      return Number(loadedId);
    }

    await invalidateSettingsAsync();

    const refreshedId =
      queryClient.getQueryData<SettingsQueryResult>(CACHE_KEY)?.siteSettings
        ?.user?.id;

    return refreshedId ? Number(refreshedId) : undefined;
  }, [
    queryResult?.siteSettings?.user?.id,
    invalidateSettingsAsync,
    queryClient,
  ]);

  return {
    settings: queryResult?.siteSettings,
    userSessionData: queryResult?.userSessionData,
    invalidateSettings,
    invalidateSettingsAsync,
    getUserIdAsync,
    isLoading,
    isFetching,
    error,
  };
};
